import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService, SessionService } from '@services';
import { Observable, Subject, catchError, switchMap, take, throwError } from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly REFRESH_THRESHOLD = 300;
  private isRefreshing = false;
  private refreshSubject = new Subject<boolean>();

  constructor(
    private session: SessionService,
    private router: Router,
    private authService: AuthService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.includes('oauth/token')) {
      return next.handle(req);
    }

    const authToken = this.session.currentAuthToken;
    const authTokenType = this.session.currentAuthTokenType;
    const authTokenExpires = this.session.currentAuthTokenExpires;

    if (
      authToken &&
      authTokenExpires &&
      authTokenExpires - Math.floor(Date.now() / 1000) < this.REFRESH_THRESHOLD
    ) {
      return this.refreshAndProceed(req, next);
    }

    if (authToken) {
      req = req.clone({
        setHeaders: {
          Authorization: `${authTokenType} ${authToken}`,
        },
      });
    }

    return next.handle(req).pipe(
      catchError((response: HttpErrorResponse) => {
        //----------------------------------------------
        const browserURL = window.location.pathname;
        const isDataViewEditRoute = browserURL.includes('/feed') && browserURL.includes('/edit');
        //----------------------------------------------
        if (response.status === 401 && !isDataViewEditRoute) {
          return this.refreshAndProceed(req, next);
        }
        return throwError(() => response);
      }),
    );
  }

  private refreshAndProceed(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshSubject = new Subject<boolean>();
      this.authService.refreshAccessToken().subscribe({
        next: () => {
          this.isRefreshing = false;
          this.refreshSubject.next(true);
        },
        error: () => {
          this.isRefreshing = false;
          this.refreshSubject.next(false);
          this.authService.logout();
          this.router.navigate(['/']);
        },
      });
    }

    return this.refreshSubject.pipe(
      take(1),
      switchMap((refreshed) => {
        if (!refreshed) {
          return throwError(() => new Error('Failed to refresh access token'));
        }
        const newToken = this.session.currentAuthToken;
        const newTokenType = this.session.currentAuthTokenType;
        return next.handle(
          req.clone({
            setHeaders: {
              Authorization: `${newTokenType} ${newToken}`,
            },
          }),
        );
      }),
    );
  }
}
